import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Bird, Smartphone, BookHeart, Target, GraduationCap, Sparkles } from "lucide-react";
import heroImage from "@/assets/blog/self-growth-apps-college.jpg";
import productivityAppsImage from "@/assets/blog/productivity-apps-college.jpg";
import organizationAppsImage from "@/assets/blog/organization-apps-college.jpg";

const tocItems = [
  { id: "finch", title: "Finch - Self-Care Pet Companion" },
  { id: "one-sec", title: "One Sec - Mindful Phone Usage" },
  { id: "daylio", title: "Daylio - Mood & Micro-Diary" },
  { id: "habit", title: "Habit - Habit Tracker" },
  { id: "conclusion", title: "Conclusion" },
];

const AppsBoostSelfGrowthCollege = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Apps that Boosted my Self-growth in College"
        description="Discover the best self-growth apps for college students. From mental wellness and mindful phone usage to mood tracking and habit building, these 4 apps will help you grow."
        keywords="self-growth apps, college apps, Finch app, One Sec, Daylio, Habit tracker, mental wellness apps, self-care apps, student wellbeing"
        canonicalUrl="/blog/apps-boost-self-growth-college/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Self-Growth
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>December 16, 2024</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>12 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              Apps that Boosted my Self-growth in College!
            </h1>

            {/* Featured Image */}
            <img 
              src={heroImage} 
              alt="Self-care setup with phone showing wellness apps" 
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
              
            {/* Lead Paragraph */}
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
              Starting college is an exciting time. It's full of new experiences, learning, and eventually growing into your best self. But, the truth is, college can also be overwhelming. Between classes, social life, and everything else going on, it's easy to lose track of our goals or forget to take care of ourselves. If you're struggling with staying on track, check out our guide on <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>how to stop procrastination</Link>.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              That's why as a college student myself, I rely on some apps to help me get through the semester without burning out. Over the past few years, I've tried out numerous apps that are specifically focused on self-growth. Eventually, I stuck with four apps that have helped me stay sane throughout college. These apps help me create small daily routines that add up to big changes, whether it's managing stress, staying active, or just remembering to pause and reflect. For more productivity tools, also check out our <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>top 10 digital tools for academic success</Link>.
            </p>

            {/* Mobile TOC */}
            <div className="lg:hidden mb-12">
              <BlogTableOfContents items={tocItems} />
            </div>

            {/* Section 1: Finch */}
            <div id="finch" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bird className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Finch - Self-Care Pet Companion
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              One app that has become an absolute must-have for me is the app called <a href="https://finchcare.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Finch</a>. At its core, Finch is a virtual pet bird that you take care of by practicing self-care and building positive habits. It's basically kind of like a Tamagotchi for your mental and emotional wellness. You pick a cute bird, name it, and then help it grow stronger by completing small daily actions called "journeys." These can be quick breathing exercises, journaling prompts, mood check-ins, or simple tasks like drinking water or writing down a gratitude note.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              What makes Finch stand out is how it gamifies self-care in a way that doesn't feel overwhelming or clinical. Instead of a long to-do list, the app gently nudges you to take small, manageable steps, and each time you complete an activity, your Finch perks up and earns "rainbow stones" which can help you customize your pet's world. This fun and supportive design has helped me turn self-care into a habit without pressure or guilt.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Finch's mood tracker is something I appreciate. It basically checks in on how you're feeling multiple times a day and then eventually visualizes patterns over time to help you uncover emotional triggers you might not notice. Aside from that, the app also includes guided breathing exercises, sleep tools, as well as motivational messages and positive affirmations that have come in handy during stressful times. For me, Finch is like having a tiny and encouraging friend that's here to cheer me on to take just five minutes for myself, which I know as a college student, often ends up feeling like we don't deserve it.
            </p>

            {/* Section 2: One Sec */}
            <div id="one-sec" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    One Sec - Mindful Phone Usage
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              One app that has genuinely helped me regain control over my phone habits is <a href="https://one-sec.app" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">One Sec</a>. If scrolling endlessly through social media feeds has ever felt like a mindless reflex (because, same here), then One Sec might be a game-changer. When you try to open one of your selected apps, like Instagram or TikTok, One Sec immediately prompts you to take a deep, calming breath—just for about five seconds—and then asks if you still really want to open the app.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              This brief pause gives me a moment to reflect on whether I really want to open the app simply out of habit, boredom, or because of genuine intention. Aside from that, the app also tracks how many times you've tried to open each app and shows you how much time you've saved by breaking these unconscious habits. This app really helped me stop mindless scrolling and turn it into mindful choices. Instead of falling for the usual trap, I started becoming more aware with each app that I opened.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              One Sec's approach is based on real science too. A study showed that users reduced app openings by over 50% within six weeks. While some features are behind a premium paywall, the free version still offers powerful tools to make you think twice before getting lost in apps. In college, where mental breaks are gold, One Sec feels like a gentle but effective coach that nudges me toward being more present and intentional with how I spend my breaks.
            </p>

            {/* Section 3: Daylio */}
            <div id="daylio" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BookHeart className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Daylio - Mood & Micro-Diary
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              If Finch allows me to take care of myself, then <a href="https://daylio.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Daylio</a> allows me to become more in touch with my emotions and feelings. If you're like me, journaling doesn't work—and I mean that literally. I've tried journaling so many times (looks at the pile of notebooks on the side of my room) and I always end up not seeing it through. Daylio offers a simple, stress-free way to check in with yourself. It's basically a micro-diary where you can select your moods from a range of emojis and quickly add activities or habits that you did that day, all without needing to write a single sentence unless you want to. For me, this makes it super easy to stay consistent even on my busiest day.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Eventually, Daylio takes these tiny daily emotions you've recorded into meaningful patterns. I started noticing how certain habits or actions affected my mood. Whether it was a late night of studying, going out with people I don't normally talk to, or just taking some "me" time, Daylio shows these to me by making visual charts and statistics that show my mood trends over days, weeks, or months. Daylio also lets you set goals and track habits like exercise, meditation, or reading and helps you get that gentle push to keep you moving forward without feeling overwhelmed.
            </p>

            {/* Section 4: Habit */}
            <div id="habit" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Habit - Habit Tracker
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Okay, let's get into my number one app when it comes to starting habits. Habits are important when it comes to self-growth, especially if you have goals that aren't just academic. James Clear's Atomic Habit states "You do not rise to the level of your goals. You fall to the level of your systems."
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              One app that has truly helped me stick to the habits I want to build is Habit. If you've ever struggled with consistency, Habit makes the process straightforward and motivating. It's a clean, no-nonsense habit tracker that lets you set daily, weekly, or custom habits and reminds you gently to keep going. What I like most is how it breaks down big goals into tiny, manageable steps. If you want to combine habit tracking with focused study sessions, try using a <Link to="/study-timer/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>pomodoro timer</Link> to structure your day.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              For example, one of my goals for this year is to improve my French. Instead of just having a general sense of the goal, Habit has allowed me to commit to small, consistent actions like doing a quick vocabulary review or listening to a podcast episode daily. Aside from that, my goals have become more actionable. Instead of just having a goal named "improve my French," I've changed it to "Reach B2 Level for French." You can also set reminders, track your progress with streaks and charts, and even customize your habit list to fit whatever goals you're working on. Habit has helped me build tiny behaviors that I'm sure can become real change. Using this app helps me remind myself that growth isn't about giant leaps but instead little steps taken consistently. For more productivity tips, check out our <Link to="/blog/apps-boost-productivity-college/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>apps that boost productivity in college</Link>.
            </p>

            {/* Conclusion */}
            <h2 id="conclusion" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Conclusion
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              So that's it! Those are the four apps that have helped me boost my self-growth while in College. When I was a freshman, I thought I would never rely on apps like these. But here we are! Building good habits and taking care of yourself during college doesn't have to be complicated or overwhelming.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              Aside from that, these apps don't work unless you yourself give it a try and you actually become consistent with it. Of course, I understand that not all of these apps might work for everyone. My advice is to try one app first for a week, see how it goes, and if it works then hey! Remember growth happens one small habit at a time, and with the right tools, it's absolutely within reach.
            </p>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 md:p-12 text-center">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-6" />
              <h3 className="text-2xl md:text-3xl font-display font-bold mb-4">
                Ready to Start Your Self-Growth Journey?
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-8 max-w-xl mx-auto">
                Combine these self-growth apps with CSW's free 24/7 online study rooms to build better habits and stay accountable with a supportive community.
              </p>
              <a 
                href="https://app.csw.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-lg hover:bg-primary/90 transition-colors"
              >
                Try CSW for Free
              </a>
            </div>

            {/* Related Articles Section */}
            <div className="mt-20">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 tracking-tight">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Link 
                  to="/blog/apps-boost-productivity-college/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <img 
                    src={productivityAppsImage} 
                    alt="Apps that boost productivity in college" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-5">
                    <span className="text-sm text-primary font-medium font-body mb-2 block">Productivity</span>
                    <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                      Apps that Boost my Productivity in College!
                    </h3>
                  </div>
                </Link>

                <Link 
                  to="/blog/apps-boost-organization-college/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <img 
                    src={organizationAppsImage} 
                    alt="Apps that boost organization in college" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-5">
                    <span className="text-sm text-primary font-medium font-body mb-2 block">Productivity</span>
                    <h3 className="text-lg font-display font-bold group-hover:text-primary transition-colors">
                      Apps that Boost my Organization in College!
                    </h3>
                  </div>
                </Link>
              </div>
            </div>

          </div>
          
          {/* Floating Sticky TOC - Desktop only */}
          <aside className="hidden lg:block fixed right-8 xl:right-16 top-24 z-40">
            <BlogTableOfContents items={tocItems} collapsible />
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default AppsBoostSelfGrowthCollege;
